import { PlusOutlined } from "@ant-design/icons"
import { Button, Drawer, Flex, Grid, Modal, Tag, Typography } from "antd"
import dayjs from "dayjs"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../../../../../hooks/use-app-dispatch.ts"
import { useDateFormatter } from "../../../../../../../hooks/use-date-formatter.tsx"
import { deletePayment } from "../../../../../redux/project-finance/project-finance.thunks.ts"
import type {
  FinancialTransactionItem,
  Payment
} from "../../../../../types/project-finance.types.tsx"
import {
  FINANCE_ITEM_TYPES,
  isFinanceItemLocked
} from "../../../../constants/finance-item-constants.ts"
import { PaymentsHistoryList } from "./PaymentsHistoryList.tsx"
import { PaymentsSummary } from "./PaymentsSummary.tsx"
import type { PaymentModalState } from "./payment-modal.types.ts"

interface TransactionPaymentsDrawerProps {
  record: FinancialTransactionItem | null
  open: boolean
  projectId: string
  currentUserId: string
  paymentEditAllowed?: boolean
  uploadFileAllowed?: boolean
  deleteFileAllowed?: boolean
  deleteForeignFileAllowed?: boolean
  onClose: () => void
  onOpenPaymentModal: (state: PaymentModalState) => void
  onPaymentChanged?: () => void
}

export const TransactionPaymentsDrawer: React.FC<
  TransactionPaymentsDrawerProps
> = ({
  record,
  open,
  projectId,
  currentUserId,
  paymentEditAllowed = false,
  uploadFileAllowed,
  deleteFileAllowed,
  deleteForeignFileAllowed,
  onClose,
  onOpenPaymentModal,
  onPaymentChanged
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const screens = Grid.useBreakpoint()
  const { timezone, dateFormat } = useDateFormatter()
  const [deleting, setDeleting] = useState<string | null>(null)

  const handleDelete = (payment: Payment) => {
    if (!record) return
    Modal.confirm({
      title: t("finance.paymentsDrawer.deleteConfirm.title"),
      content: t("finance.paymentsDrawer.deleteConfirm.content"),
      okText: t("finance.paymentsDrawer.deleteConfirm.ok"),
      okType: "danger",
      onOk: async () => {
        setDeleting(payment.id)
        try {
          await dispatch(
            deletePayment({ transactionId: record.id, paymentId: payment.id })
          ).unwrap()
          onPaymentChanged?.()
        } catch {
          // surfaced via global error handling
        } finally {
          setDeleting(null)
        }
      }
    })
  }

  const payments = record?.partial_finances ?? []
  const counterparty =
    record?.subcontractor_name ||
    record?.custom_subcontractor_name ||
    record?.assignee_name ||
    null
  const isReceivable = record?.item_type === FINANCE_ITEM_TYPES.RECEIVABLE

  return (
    <Drawer
      title={record?.name}
      placement="right"
      width={screens.xl ? 1040 : screens.lg ? 800 : screens.md ? 640 : "100%"}
      open={open}
      onClose={onClose}
      destroyOnHidden
    >
      {record && (
        <Flex vertical gap={16}>
          <Flex gap="small" align="center" wrap>
            <Tag color={isReceivable ? "blue" : "orange"}>
              {record.item_type_display}
            </Tag>
            {record.due_date && (
              <Typography.Text type="secondary">
                {t("finance.paymentsDrawer.due")}:{" "}
                {dayjs.utc(record.due_date).tz(timezone).format(dateFormat)}
              </Typography.Text>
            )}
          </Flex>
          {counterparty && (
            <Typography.Text type="secondary">
              {record.responsible_type_display}: {counterparty}
            </Typography.Text>
          )}

          <PaymentsSummary record={record} />

          <Flex vertical gap={8}>
            <Flex justify="space-between" align="center">
              <Typography.Title level={5} className="!mb-0">
                {t("finance.paymentsDrawer.history")}
              </Typography.Title>
              {paymentEditAllowed && !isFinanceItemLocked(record.status) && (
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => onOpenPaymentModal({ mode: "create" })}
                  loading={deleting !== null}
                >
                  {t("finance.paymentsDrawer.addPayment")}
                </Button>
              )}
            </Flex>
            {payments.length > 0 ? (
              <PaymentsHistoryList
                payments={payments}
                currentUserId={currentUserId}
                projectId={projectId}
                uploadFileAllowed={uploadFileAllowed}
                deleteFileAllowed={deleteFileAllowed}
                deleteForeignFileAllowed={deleteForeignFileAllowed}
                onEdit={
                  paymentEditAllowed
                    ? (p) => onOpenPaymentModal({ mode: "edit", payment: p })
                    : undefined
                }
                onDelete={paymentEditAllowed ? handleDelete : undefined}
              />
            ) : (
              <Typography.Text type="secondary">
                {t("finance.paymentsDrawer.empty")}
              </Typography.Text>
            )}
          </Flex>
        </Flex>
      )}
    </Drawer>
  )
}
